import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react'
import { CaretDown, Check, CircleNotch, X } from '@/components/icons'
import { cn } from '@/lib/cn'

export const DropdownMenu = DropdownMenuPrimitive.Root
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
export const DropdownMenuGroup = DropdownMenuPrimitive.Group

export const DropdownMenuContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(function DropdownMenuContent({ className, sideOffset = 4, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'bf-popover-motion z-50 min-w-[10rem] max-h-[min(24rem,calc(100dvh-5rem))] overflow-y-auto rounded-lg',
          // On phones the trigger spans its grid cell, so the menu should be
          // at least that wide rather than hanging off one corner of it.
          'max-md:min-w-[var(--radix-dropdown-menu-trigger-width)]',
          'bg-[color:var(--popover)] p-1 text-[13px] text-[color:var(--popover-foreground)] shadow-[var(--shadow-overlay)]',
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
})

/**
 * The "Label   Value ⌄" button every queue filter opens from. One class so the
 * PO and SO queues read as one product; on phones it fills its grid cell with
 * the label pinned left and the value + caret pinned right.
 */
export const DropdownMenuFilterTrigger = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> & {
    /** The muted filter name (Status, Channel, Vendor…). */
    label: ReactNode
    /** Swap the caret for a spinner while the option list loads. */
    loading?: boolean
  }
>(function DropdownMenuFilterTrigger({ className, label, loading, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex h-8 items-center rounded-md bg-[color:var(--card)] px-3 text-[12.5px] font-medium shadow-[var(--shadow-raised)]',
        'max-md:min-h-11 max-md:w-full',
        'hover:bg-[color:var(--muted)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]',
        className,
      )}
      {...props}
    >
      <span className="mr-4 truncate text-[color:var(--muted-foreground)] max-md:mr-auto">
        {label}
      </span>
      {children}
      {loading ? (
        <CircleNotch className="size-3 shrink-0 animate-spin text-[color:var(--muted-foreground)]" />
      ) : (
        <CaretDown className="size-3 shrink-0 text-[color:var(--muted-foreground)]" />
      )}
    </DropdownMenuPrimitive.Trigger>
  )
})

export const DropdownMenuItem = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Item>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & { inset?: boolean }
>(function DropdownMenuItem({ className, inset, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 outline-none',
        'transition-colors focus:bg-[color:var(--muted)]',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        inset && 'pl-8',
        className,
      )}
      {...props}
    />
  )
})

export const DropdownMenuLabel = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(function DropdownMenuLabel({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn(
        'px-2 py-1 text-[12px] font-medium text-[color:var(--muted-foreground)]',
        className,
      )}
      {...props}
    />
  )
})

export function DropdownMenuFilterHeader({
  children,
  onClear,
}: {
  children: ReactNode
  onClear?: () => void
}) {
  return (
    <div className="flex min-h-8 items-center justify-between gap-4">
      <DropdownMenuLabel className="min-w-0">{children}</DropdownMenuLabel>
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          className="mr-1 inline-flex items-center gap-1 rounded-sm px-1.5 py-1 text-[11.5px] font-medium text-[color:var(--muted-foreground)] transition-colors hover:bg-[color:var(--muted)] hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]"
        >
          Clear
          <X aria-hidden className="size-3" />
        </button>
      ) : null}
    </div>
  )
}

export const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn('my-1 h-px bg-[color:var(--border)]', className)}
      {...props}
    />
  )
})

export const DropdownMenuCheckboxItem = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, checked, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      checked={checked}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 pl-7 pr-2 outline-none',
        'transition-colors focus:bg-[color:var(--muted)]',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="absolute left-1.5 inline-flex size-3.5 items-center justify-center rounded-[4px] border border-[color:var(--input)] bg-[color:var(--card)] transition-colors">
        <DropdownMenuPrimitive.ItemIndicator className="flex size-full items-center justify-center rounded-[3px] bg-[color:var(--primary)] text-[color:var(--primary-foreground)]">
          <Check aria-hidden className="size-2.5" weight="bold" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
})
